import { useSelector } from "react-redux";
import { PermissionAction } from "../flatbuffers/generated/archive/permission-action";
import { isScopeAllowed, ScopeTarget } from "../utils/scopePermission";
import { selectScopeState } from "../redux/scope/scopeSlice";
import { isMasterAdmin } from "../utils/isMasterAdmin";
import { hasPermission } from "../types/permissions";
import type { Permission } from "../types/permissions";
import { isScreenAllowed } from "../types/screenPermissions";
import { canOpenControlPanel } from "../types/monitoringAccess";
import type { ControlPanelKind } from "../types/monitoringAccess";
import type { ScreenId } from "../docs/screens.generated";
import type { RootState } from "../app/store";

/**
 * Returns true when the current user's scope allows the given action on the
 * supplied target dimensions. IsMasterAdmin short-circuits to true.
 *
 * Use this for guards on individual entities (e.g. a single button).
 */
export function useScopedPermission(action: PermissionAction, target: ScopeTarget): boolean {
  const scope = useSelector(selectScopeState);
  const userLogged = useSelector((state: RootState) => state.home.userLogged);

  if (isMasterAdmin(userLogged)) return true;
  return isScopeAllowed(scope, action, target);
}

/**
 * Returns a predicate function suitable for `.filter()` loops over arrays of
 * entities. IsMasterAdmin short-circuits to an always-true predicate.
 *
 * Usage:
 *   const allowed = useScopePredicate(PermissionAction.VIEW);
 *   const visible = banks.filter(b => allowed({ bankId: b.id }));
 */
export function useScopePredicate(action: PermissionAction): (target: ScopeTarget) => boolean {
  const scope = useSelector(selectScopeState);
  const userLogged = useSelector((state: RootState) => state.home.userLogged);

  if (isMasterAdmin(userLogged)) {
    return () => true;
  }

  return (target: ScopeTarget) => isScopeAllowed(scope, action, target);
}

/**
 * Combined feature-menu AND scope check.
 *
 * Returns true when:
 *   1. The user is MASTER ADMIN, which is denied nothing; or
 *   2. The user's role menu has the given feature permission enabled, AND
 *      the user's scope allows the given action on the given target.
 *
 * The master-admin check runs FIRST, ahead of the menu gate. It used to run
 * after, on a belt-and-suspenders argument: a master-admin role whose
 * permissions were "accidentally removed" would still obey the menu. In
 * practice that reasoning inverted the intent of the role and produced a
 * failure nobody could diagnose.
 *
 * A role's stored `menu` used to be a SNAPSHOT of the permission catalogue
 * taken when the role was written. Adding a catalogue entry did not reach back
 * into menus already stored, so every role written earlier -- MASTER ADMIN
 * included -- had no node for the new key, and `hasPermission` is strict set
 * membership, so that absence read as an explicit denial. (LiftCloud now
 * renders every role's menu from the whole catalogue on each read.) Nothing was
 * "accidentally removed"; the permission simply did not exist yet.
 *
 * That is what killed `monitor-control-panel-banks` and
 * `monitor-control-panel-lifts`: both present and enabled in the catalogue,
 * absent from every menu stored before they were added, so the bank and lift
 * control panels stopped opening for everyone including master admin. The click
 * handlers return without a word, so nothing surfaced in the browser console or
 * any server log.
 *
 * Master admin is now unconditional, which is what the role means. The menu
 * gate remains for every other role: a permission absent from a role's menu is
 * still a denial there, so this grants nothing to unprivileged users.
 */
export function useEffectivePermission(
  featurePermission: Permission | string,
  action: PermissionAction,
  target: ScopeTarget,
): boolean {
  const scope = useSelector(selectScopeState);
  const userLogged = useSelector((state: RootState) => state.home.userLogged);
  const permissionSet = useSelector((state: RootState) => state.user.permissionSet);

  if (isMasterAdmin(userLogged)) return true;

  const menuAllowed = hasPermission(featurePermission, permissionSet ?? []);
  if (!menuAllowed) return false;

  return isScopeAllowed(scope, action, target);
}

/**
 * Feature-menu check that honours MASTER ADMIN, with no scope component.
 *
 * The drop-in replacement for a bare
 * `hasPermission(Permission.X, permissionSet)` at a component call site.
 * `hasPermission` is a pure set lookup and cannot know who is asking, so every
 * raw call site silently denies master admin -- which is how the lift and bank
 * drawers came to ignore clicks for the one role that is supposed to be able to
 * do everything.
 *
 * Use this in components. Reserve bare `hasPermission` for pure helpers that
 * are handed a permission set and have no access to the store.
 */
export function useMenuPermission(featurePermission: Permission | string): boolean {
  const userLogged = useSelector((state: RootState) => state.home.userLogged);
  const permissionSet = useSelector((state: RootState) => state.user.permissionSet);

  if (isMasterAdmin(userLogged)) return true;
  return hasPermission(featurePermission, permissionSet ?? []);
}

/**
 * Screen-level feature-menu check that honours MASTER ADMIN, reading its
 * requirement from `SCREEN_PERMISSION` (see `types/screenPermissions.ts`)
 * instead of a permission hand-written at the call site.
 *
 * The drop-in replacement for a page's own
 * `hasPermission(Permission.X, permissionSet ?? [])` guard -- the
 * same `id` is what Layout.tsx's sidebar reads (via the plain
 * `isScreenAllowed` there, since the menu builder runs inside a `useMemo`
 * and cannot call this hook), so the two can no longer name different
 * permissions for the same screen, and both share the same MASTER ADMIN
 * bypass via `isScreenAllowed` rather than each re-deriving it.
 */
export function useScreenPermission(id: ScreenId): boolean {
  const userLogged = useSelector((state: RootState) => state.home.userLogged);
  const permissionSet = useSelector((state: RootState) => state.user.permissionSet);

  return isScreenAllowed(id, userLogged, permissionSet ?? []);
}

/**
 * May this user open the bank or lift control panel at all?
 *
 * True when at least one of the panel's tabs would render (see
 * CONTROL_PANEL_TAB_GATES), MASTER ADMIN always. This replaces the old
 * `monitor-control-panel-banks` / `monitor-control-panel-lifts` grants: opening
 * is now DERIVED from the tab permissions the Roles editor's Monitoring
 * Dashboard switches manage, so there is no second switch to forget.
 */
export function useCanOpenControlPanel(kind: ControlPanelKind): boolean {
  const userLogged = useSelector((state: RootState) => state.home.userLogged);
  const permissionSet = useSelector((state: RootState) => state.user.permissionSet);

  return canOpenControlPanel(kind, permissionSet ?? [], isMasterAdmin(userLogged));
}
